import React from 'react'
import { useTranslation } from 'react-i18next'
import { XCircle } from 'lucide-react'
import { Order, OrderStatus } from '@/types'
import CurrencyDisplay from '../shared/CurrencyDisplay'
import user from "../../assets/images/users.svg";
import doIcon from "../../assets/images/do.svg";
import loIcon from "../../assets/images/lo.svg";
import partialIcon from "../../assets/images/partial.svg";

interface OrderCardNewProps {
  order: Order
  onCardClick?: (order: Order) => void
  isRTL?: boolean
  isSelected?: boolean
  onConfirmPayment?: (order: Order) => void
}

const OrderCardNew: React.FC<OrderCardNewProps> = ({
  order,
  onCardClick,
  isSelected = false,
  onConfirmPayment,
}) => {
  const { t } = useTranslation()

  const getStatusConfig = (status: OrderStatus, statusLabel?: string, statusKey?: string) => {
    // Check if statusLabel is a translation key (starts with 'orders.')
    // If it's a key, translate it; otherwise use it directly (API-provided label)
    let label: string
    if (statusLabel) {
      if (statusLabel.startsWith('orders.status.') || statusLabel.startsWith('orders.')) {
        // It's a translation key
        label = t(statusLabel)
      } else {
        // It's an API-provided translated label, use it directly
        label = statusLabel
      }
    } else if (statusKey) {
      // Use the translation key
      label = t(statusKey)
    } else {
      // Fallback to status-based translation
      switch (status) {
        case 'completed':
          label = t('orders.status.completed')
          break
        case 'preparing':
        case 'ready':
          label = t('orders.status.active')
          break
        case 'pending':
          label = t('orders.status.pending')
          break
        case 'cancelled':
          label = t('orders.status.cancelled')
          break
        default:
          label = status
      }
    }

    switch (status) {
      case 'completed':
        return {
          label,
          icon: doIcon,
          bgColor: 'bg-neutral-100',
          textColor: 'text-black',
        }
      case 'preparing':
      case 'ready':
        return {
          label,
          icon: partialIcon,
          bgColor: 'bg-neutral-100',
          textColor: 'text-black',
        }
      case 'pending':
        return {
          label,
          icon: loIcon,
          bgColor: 'bg-neutral-100',
          textColor: 'text-black',
        }
      case 'cancelled':
        return {
          label,
          icon: XCircle,
          bgColor: 'bg-neutral-100',
          textColor: 'text-black',
        }
      default:
        return {
          label,
          icon: partialIcon,
          bgColor: 'bg-neutral-100',
          textColor: 'text-black',
        }
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${hours}:${minutes}:${seconds} ${year}-${month}-${day}`
    } catch {
      return dateString
    }
  }
  
  const getOrderTypeLabel = (order: Order & { orderTypeLabel?: string; orderTypeKey?: string }) => {
    // Check if orderTypeLabel is a translation key (starts with 'orders.')
    // If it's a key, translate it; otherwise use it directly (API-provided label)
    if (order.orderTypeLabel) {
      if (order.orderTypeLabel.startsWith('orders.type.') || order.orderTypeLabel.startsWith('orders.')) {
        // It's a translation key
        return t(order.orderTypeLabel)
      } else {
        // It's an API-provided translated label, use it directly
        return order.orderTypeLabel
      }
    } else if (order.orderTypeKey) {
      // Use the translation key
      return t(order.orderTypeKey)
    } else {
      // Fallback to order_type-based translation
      switch (order.order_type) {
        case 'delivery':
          return t('orders.type.delivery')
        case 'takeaway':
          return t('orders.type.takeaway')
        case 'dine_in':
          return t('orders.type.dine_in')
        case 'car_pickup':
          return t('orders.type.car_pickup')
        default:
          return t('orders.type.takeaway')
      }
    }
  }
  
  const getPaymentMethodLabel = (method?: string) => {
    if (!method) return ''
    const methodLower = method.toLowerCase()
    if (methodLower.includes('cash') || methodLower.includes('كاش')) return t('payment.cash')
    if (methodLower.includes('card') || methodLower.includes('شبكة') || methodLower.includes('network')) return t('payment.network')
    if (methodLower.includes('online')) return t('payment.online')
    if (methodLower.includes('partial') || methodLower.includes('دفع جزئي')) return t('payment.partial')
    if (methodLower.includes('wallet') || methodLower.includes('محفظة')) return t('payment.wallet')
    return method
  }

  const getPlatformLabel = (platform?: string) => {
    if (!platform) return ''
    const platformKey = platform.toLowerCase()
    switch (platformKey) {
      case 'ios':
        return t('orders.platform.ios')
      case 'android':
        return t('orders.platform.android')
      case 'web':
        return t('orders.platform.web')
      case 'pos':
        return t('orders.platform.pos')
      case 'qr':
        return t('orders.platform.qr')
      case 'cashier':
        return t('orders.platform.cashier')
      case 'customer_service':
        return t('orders.platform.customer_service')
      default:
        return platform
    }
  }

  const formatPrice = (price: number) => {
    // Always display in English numerals
    return price.toFixed(2)
  }

  // Check if order is a cash collection order (takeaway / dine_in)
  const isCashCollectionOrder = (order: Order): boolean => {
    const isCashPayment = order.payment_method === 'cash'
    const isCollectionType = order.order_type === 'takeaway' || order.order_type === 'dine_in'
    return isCashPayment && isCollectionType
  }

  // Check if order is eligible for payment confirmation
  const canConfirmPayment = (order: Order): boolean => {
    const isPaymentPending = order.payment_status === 'pending' || order.payment_status === 'partial'
    return isCashCollectionOrder(order) && isPaymentPending
  }

  const showConfirmPayment = canConfirmPayment(order)
  const showPaidMessage = isCashCollectionOrder(order) && order.payment_status === 'paid'

  const orderWithLabels = order as Order & { statusLabel?: string; orderTypeLabel?: string; statusKey?: string; orderTypeKey?: string }
  const statusConfig = getStatusConfig(order.status, orderWithLabels.statusLabel, orderWithLabels.statusKey)
  const StatusIcon = statusConfig.icon

  // Helper function to safely extract string value
  const getBranchName = (): string => {
    if (!order.branch) return t('orders.default_branch')
    if (typeof order.branch === 'string') return order.branch
    if (typeof order.branch === 'object' && 'name' in order.branch) {
      return order.branch.name || t('orders.default_branch')
    }
    return t('orders.default_branch')
  }

  // Build order details array with all available data
  // Extract payment method - handle both string and object formats
  const paymentMethodValue = 
    (order as Order & { paymentMethod?: string }).paymentMethod || 
    (typeof order.payment_method === 'string' ? order.payment_method : 
     (order.payment_method && typeof order.payment_method === 'object' && 'name' in order.payment_method 
      ? (order.payment_method as any).name : order.payment_method)) ||
    (order as any).payment_method_text ||
    (order as any).paymentMethodText
  const paymentMethodLabel = paymentMethodValue ? getPaymentMethodLabel(String(paymentMethodValue)) : ''
  const platformLabel = getPlatformLabel(order.platform)
  const tableName = order.table?.name || order.table?.number || ''

  const orderDetails = [
    typeof (order.creator || order.cashier?.name) === 'string' ? (order.creator || order.cashier?.name) : '',
    getBranchName(),
    getOrderTypeLabel(orderWithLabels),
    tableName ? `${t('orders.table')}: ${tableName}` : '',
    `#${order.order_number}`,
    formatDate(order.created_at || order.dateTime || ''),
    `${order.itemsCount || order.items?.length || 0} ${t('orders.items_count_label')}`,
    paymentMethodLabel ? paymentMethodLabel : '',
    platformLabel ? platformLabel : '',
  ].filter((item): item is string => typeof item === 'string' && item !== '')

  return (
    <div
      onClick={() => onCardClick?.(order)}
      className={`p-4 bg-white rounded-2xl flex flex-col cursor-pointer transition-all h-full ${
        isSelected 
          ? 'ring-2 ring-flutter-button shadow-lg' 
          : 'hover:shadow-md'
      }`}
    >
      {/* Top Section */}
      <div className="flex flex-col gap-4 flex-shrink-0">
        {/* Header: Status and Customer */}
        <div className={`flex justify-between items-center flex-row-reverse flex-shrink-0`}>
          {/* Status Badge */}
          <div className={`px-2 py-0.5 ${statusConfig.bgColor} rounded-lg flex items-center gap-1 flex-row-reverse flex-shrink-0`}>
            <span className={`text-sm font-medium font-['IBM Plex Sans Arabic'] ${statusConfig.textColor} `}>
              {statusConfig.label}
            </span>
            {typeof StatusIcon === 'string' ? (
              <img src={StatusIcon} alt={statusConfig.label} className="w-3.5 h-3.5" />
            ) : (
              <StatusIcon className="w-3.5 h-3.5 text-black" />
            )}
          </div>

          {/* Customer Info */}
          <div className={`flex-1 flex items-center gap-3 flex-row-reverse min-w-0`}>
            <div className={`flex-1 flex flex-col gap-1 items-start min-w-0`}>
              <div className={`text-base font-medium font-['IBM Plex Sans Arabic'] text-text-primary truncate w-full`}>
                {order.customerName || order.customer?.name || 'عبد اللطيف مجدي ياقوت'}
              </div>
              <div className={`text-sm font-normal font-['IBM Plex Sans Arabic'] text-text-secondary truncate w-full`}>
                {order.phoneNumber || order.customer?.phone || '0523456720'}
              </div>
            </div>
            <div className="w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <img 
                  src={user} 
                  alt="user" 
                  className="h-6 w-6" 
                />       
            </div>
          </div>
        </div>

        {/* Order Details Tags */}
        <div className={`flex items-start gap-2 flex-wrap justify-start flex-shrink-0 min-h-[2rem]`}>
          {orderDetails.map((detail, index) => (
            <div
              key={index}
              className={`px-1.5 py-1 bg-neutral-100 rounded-lg flex items-center gap-2 flex-row-reverse flex-shrink-0`}
            >
              <span className={`text-xs font-normal font-['IBM Plex Sans Arabic'] text-text-primary whitespace-nowrap`}>
                {detail}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Section - Fixed at bottom */}
      <div className="flex flex-col mt-auto pt-2">
        {/* Divider */}
        <div className="h-px bg-border-soft flex-shrink-0" />

        {/* Total */}
        <div className={`flex items-center gap-1 flex-row-reverse justify-between flex-shrink-0 pt-2`}>
          <div className={`flex items-center gap-1 flex-row-reverse`}>
            <CurrencyDisplay className="w-4 h-4" />
            <div className={`text-base font-semibold font-['IBM Plex Sans Arabic'] text-black`}>
              {formatPrice(order.total_amount || 0)}
            </div>
          </div>
          <div className={`text-base font-normal font-['IBM Plex Sans Arabic'] text-text-primary`}>
            {t('common.total')}
          </div>
        </div>

        {/* Confirm Payment Button or Paid Message */}
        {showConfirmPayment && onConfirmPayment ? (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onConfirmPayment(order)
            }}
            className="w-full mt-3 px-4 py-2 bg-flutter-button text-white rounded-lg font-medium font-['IBM Plex Sans Arabic'] hover:bg-flutter-button/90 transition-colors flex items-center justify-center gap-2"
          >
            <span className="text-sm">{t('orders.confirm_payment', 'تأكيد الدفع')}</span>
          </button>
        ) : showPaidMessage ? (
          <div className="w-full mt-3 px-4 py-2 bg-green-50 border border-green-200 rounded-lg flex items-center justify-center gap-2">
            <span className="text-sm font-medium font-['IBM Plex Sans Arabic'] text-green-700">
              {t('orders.paid', 'تم الدفع')}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default OrderCardNew

